/**
 * Clinic-timezone helpers for the web client, built on @date-fns/tz.
 *
 * The API stores every timestamp as a UTC instant. The clinic operates in its
 * own wall-clock zone (branch override, else clinic default). These helpers convert
 * between the two at the UI edges:
 *   - parsing a date+time the user picked -> a UTC ISO string to send
 *   - formatting a UTC instant from the API -> the clinic's wall-clock for display
 *
 * Prefer useClinicTimezone() in components; this module holds the pure functions.
 */
import { TZDate } from '@date-fns/tz';
import { format as formatDate } from 'date-fns';

export const DEFAULT_TIMEZONE = 'Africa/Cairo';

/** branch override, else clinic default, else app default. */
export function resolveTimezone(
  branchTz?: string | null,
  clinicTz?: string | null,
): string {
  return branchTz || clinicTz || DEFAULT_TIMEZONE;
}

/**
 * Build the UTC instant for a wall-clock date+time in `tz`.
 * `date` is a Date whose Y/M/D are read as the clinic-local calendar date
 * (use the date part the user picked); `time` is 'HH:mm'.
 */
export function clinicWallClockToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  tz: string,
): Date {
  // TZDate interprets the parts as wall-clock in `tz`; getTime() is the instant.
  return new Date(
    new TZDate(year, month - 1, day, hour, minute, 0, tz).getTime(),
  );
}

/**
 * Given a calendar date (any Date — only its local Y/M/D matter to the caller)
 * and an 'HH:mm' string, produce the UTC ISO string to send to the API,
 * interpreting the wall-clock in the clinic's zone.
 */
export function buildScheduledAtIso(
  pickedDate: Date,
  time: string,
  tz: string,
): string {
  const [hour, minute] = time.split(':').map(Number);
  return clinicWallClockToUtc(
    pickedDate.getFullYear(),
    pickedDate.getMonth() + 1,
    pickedDate.getDate(),
    hour ?? 0,
    minute ?? 0,
    tz,
  ).toISOString();
}

/** Format a UTC instant as the clinic's wall-clock using date-fns tokens. */
export function formatInClinicZone(
  instant: Date | string,
  tz: string,
  pattern: string,
): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  if (isNaN(d.getTime())) return '';
  return formatDate(new TZDate(d.getTime(), tz), pattern);
}

/** The clinic-local 'YYYY-MM-DD' for an instant. */
export function clinicDate(instant: Date | string, tz: string): string {
  return formatInClinicZone(instant, tz, 'yyyy-MM-dd');
}

/**
 * Reinterpret a Date whose *browser-local* wall-clock parts represent the
 * intended clinic wall-clock, returning the correct UTC instant.
 *
 * Use for widgets (e.g. a browser-local calendar) that hand back a Date whose
 * displayed Y/M/D H:M the user picked but which was built in the browser zone.
 * Once such widgets render directly in the clinic zone, this is unnecessary.
 */
export function reinterpretLocalAsClinic(localDate: Date, tz: string): Date {
  return clinicWallClockToUtc(
    localDate.getFullYear(),
    localDate.getMonth() + 1,
    localDate.getDate(),
    localDate.getHours(),
    localDate.getMinutes(),
    tz,
  );
}

/**
 * Convert a real UTC instant into a "floating" Date whose BROWSER-LOCAL
 * wall-clock equals the clinic's wall-clock for that instant.
 *
 * Needed for FullCalendar. Its `timeZone` option only works for named zones
 * when a `namedTimeZoneImpl` plugin (`@fullcalendar/moment-timezone` or
 * `luxon`) is registered; without one it sets `canComputeOffset = false` and
 * silently falls back to UTC. Rather than take on that dependency, the grid
 * runs in UTC and every instant handed to it is pre-shifted here, so the
 * displayed clock is the clinic's.
 *
 * The result is NOT a valid instant — only its calendar/clock fields are
 * meaningful. Always pair it with `wallClockToInstant` on the way back out
 * (drag, resize, slot click) to recover the true UTC moment.
 */
export function instantToClinicWallClock(
  instant: Date | string,
  tz: string,
): Date {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  if (isNaN(d.getTime())) return new Date(NaN);
  const z = new TZDate(d.getTime(), tz);
  // Read the clinic's clock fields, then rebuild them as UTC fields so a
  // UTC-configured calendar renders exactly those numbers.
  return new Date(
    Date.UTC(
      z.getFullYear(),
      z.getMonth(),
      z.getDate(),
      z.getHours(),
      z.getMinutes(),
      z.getSeconds(),
    ),
  );
}

/**
 * Inverse of `instantToClinicWallClock`: take a floating wall-clock Date the
 * calendar handed back and resolve it to the true UTC instant in `tz`.
 *
 * Correct across DST because `clinicWallClockToUtc` resolves the offset for
 * that specific local date rather than applying a fixed shift.
 */
export function wallClockToInstant(wallClock: Date, tz: string): Date {
  return clinicWallClockToUtc(
    wallClock.getUTCFullYear(),
    wallClock.getUTCMonth() + 1,
    wallClock.getUTCDate(),
    wallClock.getUTCHours(),
    wallClock.getUTCMinutes(),
    tz,
  );
}
